import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Phone, KeyRound, ShieldAlert, Loader2, Check, Bot, Lock, ArrowLeft } from "lucide-react";

const STEPS = ["Phone", "Code", "Password", "Discovering"];

export default function AddAccount() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [phase, setPhase] = useState("phone");
  const [phone, setPhone] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [authId, setAuthId] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/config/telegram").then((r) => setCfg(r.data)).catch(() => setCfg({ creds_available: false })); }, []);

  const sendPhone = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telegram-accounts/auth/phone", { phone }); setAuthId(data.auth_id); setPhoneMasked(data.phone_masked); setPhase("code"); toast.success("Code sent", { description: "Check your Telegram app." }); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const finish = (data) => {
    if (data.password_required) { setPhase("password"); return; }
    setPhase("discovering");
    toast.success("Account connected", { description: "Discovering your bots…" });
    setTimeout(() => navigate(`/accounts/${data.account_id}`), 2200);
  };
  const sendCode = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telegram-accounts/auth/code", { auth_id: authId, code }); finish(data); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const sendPassword = async () => {
    setBusy(true);
    try { const { data } = await api.post("/telegram-accounts/auth/password", { auth_id: authId, password }); setPassword(""); finish(data); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  if (!cfg) return null;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/accounts")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="btn-back-accounts"><ArrowLeft size={16} /> Accounts</button>
      <PageHeader title="Connect Telegram Account" desc="Authorize once with the official Telegram login. Your session is encrypted at rest." testId="add-account-header" />

      {!cfg.creds_available ? (
        <div className="bg-card border border-border rounded-lg p-8 max-w-xl text-center space-y-3" data-testid="creds-required">
          <div className="h-12 w-12 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto"><ShieldAlert size={22} /></div>
          <h3 className="font-semibold">Telegram API credentials required</h3>
          <p className="text-sm text-muted-foreground">An administrator must configure the Telegram <span className="mono">api_id</span> and <span className="mono">api_hash</span> before accounts can be connected. No bots will be created until then.</p>
        </div>
      ) : (
        <div className="max-w-md">
          <div className="flex items-center gap-2 mb-5">
            {STEPS.map((s, i) => {
              const active = STEPS.indexOf(phase.charAt(0).toUpperCase() + phase.slice(1)) === i;
              const done = STEPS.indexOf(phase.charAt(0).toUpperCase() + phase.slice(1)) > i;
              return <div key={s} className="flex items-center gap-2">
                <span className={`h-6 w-6 rounded-full text-[11px] font-semibold flex items-center justify-center ${done ? "bg-emerald-500 text-white" : active ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{done ? <Check size={12} /> : i + 1}</span>
                {i < STEPS.length - 1 && <span className="w-6 h-px bg-border" />}
              </div>;
            })}
          </div>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            {phase === "phone" && (<>
              <div className="flex items-center gap-2"><Phone size={18} className="text-primary" /><h3 className="font-semibold">Phone number</h3></div>
              <div><Label>Include country code</Label><Input data-testid="input-phone" className="mono" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+14155552671" /></div>
              <Button onClick={sendPhone} disabled={busy || phone.length < 7} data-testid="btn-send-phone" className="w-full gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Send login code</Button>
              <p className="text-xs text-muted-foreground">Telegram will send a login code to your app. We never store your code or password.</p>
            </>)}

            {phase === "code" && (<>
              <div className="flex items-center gap-2"><KeyRound size={18} className="text-primary" /><h3 className="font-semibold">Enter login code</h3></div>
              <p className="text-xs text-muted-foreground">Sent to {phoneMasked}</p>
              <Input data-testid="input-otp" className="mono tracking-widest text-lg text-center" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="12345" maxLength={7} />
              <Button onClick={sendCode} disabled={busy || code.length < 4} data-testid="btn-verify-code" className="w-full gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Verify</Button>
            </>)}

            {phase === "password" && (<>
              <div className="flex items-center gap-2"><Lock size={18} className="text-primary" /><h3 className="font-semibold">Two-factor password</h3></div>
              <p className="text-xs text-muted-foreground">Your account has a cloud (2FA) password. It is used once and never stored.</p>
              <Input type="password" data-testid="input-2fa" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              <Button onClick={sendPassword} disabled={busy || !password} data-testid="btn-verify-2fa" className="w-full gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Authorize</Button>
            </>)}

            {phase === "discovering" && (
              <div className="text-center py-6 space-y-3">
                <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mx-auto"><Bot size={22} className="animate-pulse" /></div>
                <h3 className="font-semibold">Discovering your bots…</h3>
                <p className="text-sm text-muted-foreground">Fetching every bot owned by this account.</p>
                <Loader2 className="animate-spin mx-auto text-primary" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
